import * as React from "react";
import { IconChartBar, IconTrendingDown, IconTrendingUp, IconX } from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import { DashboardCard } from "./dashboard-card";
import { MetricChart } from "./metric-chart";

/**
 * The stat strip: hairline cells inside a DashboardCard, no tiles. Every
 * page that shows figures (dashboard activity, campaigns, product insights)
 * builds them from these so the numbers read the same everywhere.
 *
 * A series is never drawn inside a cell. A strip whose metrics carry one
 * offers "Chart" in the card's title row, and picking a figure opens that
 * series as a full-width chart under the strip.
 */

/** A series needs at least this many non-zero points to say anything. */
const MIN_POINTS = 2;

export function hasEnoughPoints(series: Record<string, number> | undefined) {
  if (!series) return false;
  let nonZero = 0;
  for (const v of Object.values(series)) {
    if (Number(v) > 0) nonZero += 1;
    if (nonZero >= MIN_POINTS) return true;
  }
  return false;
}

export interface Change {
  text: string;
  direction: "up" | "down" | "flat";
}

/**
 * How a figure moved against the prior period, kept short. A first order
 * never shows up as "+100%" against nothing.
 */
export function describeChange(
  current: number,
  previous: number | undefined,
  periodDays: number,
  format: (v: number) => string
): Change {
  const prev = Number(previous) || 0;
  const cur = Number(current) || 0;
  const span = `prior ${periodDays} days`;
  if (prev === 0) return { text: `Prior ${periodDays} days: ${format(0)}`, direction: cur > 0 ? "up" : "flat" };
  if (cur === prev) return { text: `Same as ${span}`, direction: "flat" };
  const pct = Math.round(((cur - prev) / prev) * 100);
  return {
    text: `${pct > 0 ? "+" : ""}${pct}% vs ${span}`,
    direction: pct > 0 ? "up" : "down",
  };
}

/** The same sentence for a month-over-month comparison. */
export function describeMonthChange(
  current: number,
  previous: number | undefined,
  format: (v: number) => string
): Change {
  const prev = Number(previous) || 0;
  const cur = Number(current) || 0;
  if (prev === 0) return { text: `Last month: ${format(0)}`, direction: cur > 0 ? "up" : "flat" };
  if (cur === prev) return { text: "Same as last month", direction: "flat" };
  const pct = Math.round(((cur - prev) / prev) * 100);
  return {
    text: `${pct > 0 ? "+" : ""}${pct}% vs last month`,
    direction: pct > 0 ? "up" : "down",
  };
}

export interface Metric {
  id?: string;
  label: string;
  value: string;
  /** One line under the value. A Change gets a direction icon; a string is
   *  a plain note ("120 in stock"). */
  change?: Change | string;
  /** Per-day (or per-month) series behind the chart, keyed by date. */
  series?: Record<string, number>;
  /** Singular noun for the chart tooltip, e.g. "order". */
  unit?: string;
  format?: (v: number) => string;
  /** Label for a series key on the chart; defaults to "Sep 12". */
  labelFor?: (key: string) => string;
}

export function metricKey(metric: Metric, index: number) {
  return metric.id ?? `${metric.label}-${index}`;
}

export function isChartable(metric: Metric) {
  return hasEnoughPoints(metric.series);
}

export function MetricCell({
  metric,
  selected = false,
  onSelect,
  className,
}: {
  metric: Metric;
  /** This cell's series is the one open in the chart. */
  selected?: boolean;
  /** Present when the cell can open its series; the cell becomes a button. */
  onSelect?: () => void;
  className?: string;
}) {
  const change = metric.change;
  const direction = typeof change === "string" ? "flat" : change?.direction;
  const Icon = direction === "up" ? IconTrendingUp : direction === "down" ? IconTrendingDown : null;
  const text = typeof change === "string" ? change : change?.text;

  const body = (
    <>
      <div className="text-[13px] font-medium text-foreground">{metric.label}</div>
      <div className="truncate text-3xl font-semibold tabular-nums tracking-tight text-foreground">
        {metric.value}
      </div>
      {text && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {Icon && <Icon className="size-3.5 shrink-0" aria-hidden />}
          <span className="truncate">{text}</span>
        </div>
      )}
    </>
  );

  const base = cn(
    "relative flex min-w-0 flex-col gap-1 border-l border-t border-border px-5 py-4 text-left",
    className
  );

  if (!onSelect) {
    return (
      <div id={metric.id} className={base}>
        {body}
      </div>
    );
  }

  return (
    <button
      id={metric.id}
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      title={selected ? undefined : `Show ${metric.label.toLowerCase()} over time`}
      className={cn(
        base,
        "transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        selected && "bg-muted/40"
      )}
    >
      {body}
      {/* The brand rail on the picked cell's bottom edge, the sidebar's
          active mark turned on its side. */}
      {selected && (
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-[2px] bg-brand-600" />
      )}
    </button>
  );
}

/**
 * The grid the cells sit in. Cells draw their own top and left hairlines;
 * the -1px offset hides the outer left one against the card border.
 */
export function MetricStrip({
  metrics,
  columns,
  selectedKey,
  onSelect,
  className,
}: {
  metrics: Metric[];
  /** Tailwind grid classes for the widest breakpoints, e.g.
   *  "lg:grid-cols-4". Below sm it is always one column, at sm two. */
  columns: string;
  /** Key (see metricKey) of the cell whose chart is open. */
  selectedKey?: string | null;
  /** Makes cells with a chartable series clickable. */
  onSelect?: (key: string) => void;
  className?: string;
}) {
  return (
    <div className={cn("-ml-px grid grid-cols-1 sm:grid-cols-2", columns, className)}>
      {metrics.map((metric, i) => {
        const key = metricKey(metric, i);
        return (
          <MetricCell
            key={key}
            metric={metric}
            selected={selectedKey === key}
            onSelect={onSelect && isChartable(metric) ? () => onSelect(key) : undefined}
          />
        );
      })}
    </div>
  );
}

/** Skeleton for a strip: the same cells, bones where the figures go. */
export function MetricStripSkeleton({ count, columns }: { count: number; columns: string }) {
  return (
    <div className={cn("-ml-px grid grid-cols-1 animate-pulse sm:grid-cols-2", columns)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2 border-l border-t border-border px-5 py-4">
          <div className="h-3.5 w-24 rounded bg-muted" />
          <div className="h-8 w-20 rounded bg-muted" />
          <div className="h-3 w-32 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

/** The title-row control that opens or closes the chart. */
export function ChartToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={open}
      onClick={onToggle}
      className="inline-flex items-center gap-1 rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {open ? <IconX size={14} stroke={1.75} /> : <IconChartBar size={14} stroke={1.75} />}
      {open ? "Hide chart" : "Chart"}
    </button>
  );
}

/**
 * A DashboardCard holding a strip, with the chart behaviour built in:
 * "Chart" in the title row opens the first chartable series, any chartable
 * cell opens its own, and "Hide chart" or the open cell closes it.
 */
export function MetricPanel({
  id,
  title,
  metrics,
  columns,
  loading = false,
  action,
  className,
  children,
}: {
  id?: string;
  title: string;
  metrics: Metric[];
  columns: string;
  /** Show the strip skeleton instead of the figures. */
  loading?: boolean;
  /** Extra title-row controls, such as the period toggle. */
  action?: React.ReactNode;
  className?: string;
  /** Content above the strip, such as an identity row. */
  children?: React.ReactNode;
}) {
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const chartable = metrics.map((m, i) => (isChartable(m) ? metricKey(m, i) : null)).filter(Boolean) as string[];
  const openKey = selectedKey && chartable.includes(selectedKey) ? selectedKey : null;
  const openMetric = openKey ? metrics.find((m, i) => metricKey(m, i) === openKey) : undefined;

  const toggle = () => setSelectedKey(openKey ? null : (chartable[0] ?? null));
  const select = (key: string) => setSelectedKey(key === openKey ? null : key);

  return (
    <DashboardCard
      id={id}
      title={title}
      className={className}
      action={
        <>
          {action}
          {!loading && chartable.length > 0 && <ChartToggle open={!!openKey} onToggle={toggle} />}
        </>
      }
    >
      {children}
      {loading ? (
        <MetricStripSkeleton count={metrics.length} columns={columns} />
      ) : (
        <MetricStrip metrics={metrics} columns={columns} selectedKey={openKey} onSelect={select} />
      )}
      {openMetric && <MetricChart key={openKey} metric={openMetric} />}
    </DashboardCard>
  );
}
